import { API_BASE_URL } from '@/lib/api';
import type {
  RateSessionState,
  RateSubmissionStats,
  RateSubmitResult,
  RateVoteResult,
} from './types';

function sessionKey(apex: string): string {
  return `harbor_rate_session_${apex}`;
}

export function readStoredSession(apex: string): string | null {
  try {
    return window.localStorage.getItem(sessionKey(apex));
  } catch {
    return null;
  }
}

export function writeStoredSession(apex: string, session: string): void {
  try {
    window.localStorage.setItem(sessionKey(apex), session);
  } catch {
    // private mode
  }
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail || 'Something went wrong.');
  }
  return (await res.json()) as T;
}

export async function fetchNextPair(
  apex: string,
  opts?: { category?: string; pair?: string },
): Promise<RateSessionState> {
  const params = new URLSearchParams({ host: apex });
  const session = readStoredSession(apex);
  if (session) params.set('session', session);
  if (opts?.category) params.set('category', opts.category);
  if (opts?.pair) params.set('pair', opts.pair);
  const data = await readJson<RateSessionState>(
    await fetch(`${API_BASE_URL}/v1/rate/next-pair?${params.toString()}`),
  );
  writeStoredSession(apex, data.session);
  return data;
}

export async function votePair(
  apex: string,
  body: { left_id: string; right_id: string; winner_id: string },
): Promise<RateVoteResult> {
  const session = readStoredSession(apex);
  const data = await readJson<RateVoteResult>(
    await fetch(`${API_BASE_URL}/v1/rate/vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ host: apex, session, ...body }),
    }),
  );
  writeStoredSession(apex, data.session);
  return data;
}

export async function patchRateSession(
  apex: string,
  body: {
    role?: string;
    situation?: string;
    custody?: string;
    report_public_id?: string;
  },
): Promise<RateSessionState> {
  const session = readStoredSession(apex);
  if (!session) throw new Error('Start voting first.');
  return readJson<RateSessionState>(
    await fetch(`${API_BASE_URL}/v1/rate/sessions/${session}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ host: apex, ...body }),
    }),
  );
}

export async function submitScenario(
  apex: string,
  body: { body: string; category?: string },
): Promise<RateSubmitResult> {
  const session = readStoredSession(apex);
  return readJson<RateSubmitResult>(
    await fetch(`${API_BASE_URL}/v1/rate/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ host: apex, session, ...body }),
    }),
  );
}

export async function fetchSubmission(
  apex: string,
  publicId: string,
): Promise<RateSubmissionStats> {
  const session = readStoredSession(apex);
  if (!session) throw new Error('This drop is private to your session.');
  return readJson<RateSubmissionStats>(
    await fetch(
      `${API_BASE_URL}/v1/rate/submissions/${publicId}?session=${encodeURIComponent(session)}`,
    ),
  );
}
